import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { team, leadership, operationsTeam, coreValues, stats, lifecycle, industries, services } from '../mock/mockData';
import { INDUSTRY_DETAILS } from '../mock/industryDetails';
import { Typewriter, SectionLabel, CTA, FloatingActions } from './Home';
import { PageHero } from './Services';
import WhyChooseUs from '../components/WhyChooseUs';
import SoftwareLogos from '../components/SoftwareLogos';
import JsonLd from '../components/JsonLd';
import useSeo from '../hooks/useSeo';
import { useLang } from '../contexts/LanguageContext';

const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Land Drafters',
  legalName: 'Land Drafters LLC',
  url: 'https://ldglobal.co',
  logo: 'https://ldglobal.co/favicon.png',
  foundingDate: '2003',
  description:
    'Land Drafters is a globally integrated, multi-disciplinary engineering and CAD drafting consultancy serving 40+ countries with services across surveying, BIM, civil, structural, MEP, GIS, plant and architectural disciplines.',
  slogan: 'Global Reach — For All Your Needs',
  address: [
    { '@type': 'PostalAddress', addressLocality: 'Apex', addressRegion: 'NC', addressCountry: 'US' },
    { '@type': 'PostalAddress', addressLocality: 'Pune', addressRegion: 'MH', addressCountry: 'IN' }
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-786-673-2523',
    email: 'info@landdrafters.com',
    contactType: 'customer service',
    areaServed: ['US', 'IN', 'GB', 'AU', 'CA'],
    availableLanguage: ['English', 'Spanish', 'French', 'German', 'Hindi']
  },
  sameAs: [
    'https://www.linkedin.com/company/landdrafters',
    'https://www.facebook.com/landdrafters',
    'https://twitter.com/landdrafters'
  ],
  knowsAbout: [
    'Land Surveying', 'BIM Services', 'CAD Drafting', 'Civil Engineering',
    'Structural Engineering', 'MEP Engineering', 'GIS', 'Steel Detailing',
    'Architectural Design', '3D Modeling', 'Plant Engineering'
  ],
  award: 'ISO 9001:2015 Certified'
};

export const AboutUs = () => {
  useSeo({ title: 'About Us', description: 'Discover the 20-year story, leadership and engineering philosophy behind Land Drafters — 350+ engineers serving clients across 40+ countries.', path: '/about' });
  const { tt } = useLang();
  return (
  <>
    <JsonLd id="org" data={ORG_SCHEMA} />
    <PageHero title="About" highlight="Land Drafters" typewriter={['Global Reach','Engineering Excellence','Trusted Since 2003']} subtitle="A globally integrated, multi-disciplinary engineering and CAD drafting consultancy serving clients across 5 continents." breadcrumb="Who We Are" />

    <section className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionLabel>Know About Us</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-[#0e1116] leading-tight">{tt('A Multi-Disciplinary')} <span className="text-green-600">{tt('CAD Studio')}</span></h2>
          <p className="text-gray-600 mt-6 leading-relaxed">
            {tt('Land Drafters is a multi-disciplinary engineering and CAD drafting consultancy. Founded with the mission to empower businesses through talent and passion, we collaborate with our clients to design, develop, and deliver work that represents our commitment to engineering excellence.')}
          </p>
          <p className="text-gray-600 mt-4 leading-relaxed">
            {tt('With world-class development teams and deep domain expertise, we run a seamless, scalable, cost-effective offshoring model that turns complex business challenges into real business opportunities. We are more than just a service provider — we are a solutions provider for your SUCCESS.')}
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            {stats.slice(0, 3).map(s => (
              <div key={s.label} className="bg-gray-50 p-5 rounded">
                <div className="text-3xl font-black text-green-600">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{tt(s.label)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=600&h=600&fit=crop" alt="" className="rounded-lg h-72 w-full object-cover" />
          <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&h=600&fit=crop" alt="" className="rounded-lg h-72 w-full object-cover mt-8" />
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=600&fit=crop" alt="" className="rounded-lg h-72 w-full object-cover -mt-4" />
          <img src="https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=600&h=600&fit=crop" alt="" className="rounded-lg h-72 w-full object-cover mt-4" />
        </div>
      </div>
    </section>

    {/* Vision Mission */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>Vision · Mission · Philosophy</SectionLabel>
          <h2 className="text-4xl font-black text-[#0e1116]">What <span className="text-orange-500">Drives</span> Us</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: 'Eye', title: 'Our Vision', desc: 'To empower businesses worldwide through talent, passion, and integrated engineering excellence — building the infrastructure of tomorrow.' },
            { icon: 'Target', title: 'Our Mission', desc: 'Enduring partnerships that enable continuous growth for our customers, team members, and stakeholders across every project lifecycle.' },
            { icon: 'Heart', title: 'Our Philosophy', desc: 'GROW TOGETHER. We believe in collaborative partnerships rooted in trust, transparency, and shared success.' }
          ].map((v) => {
            const Ic = Icons[v.icon];
            return (
              <div key={v.title} className="bg-white rounded-lg p-8 border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="w-14 h-14 rounded bg-gray-100 text-green-700 flex items-center justify-center mb-5"><Ic className="w-7 h-7" /></div>
                <h3 className="font-black text-xl text-[#0e1116]">{v.title}</h3>
                <p className="text-gray-600 text-sm mt-3 leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Core Values */}
    <section className="py-20 bg-[#0e1116] text-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>Our Core Values</SectionLabel>
          <h2 className="text-4xl font-black">The <span className="text-orange-500">DNA</span> of Land Drafters</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {coreValues.map(c => {
            const Ic = Icons[c.icon] || Icons.Sparkles;
            return (
              <div key={c.title} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-6 text-center hover:border-orange-500 hover:bg-[#1a1e24] transition-all">
                <Ic className="w-9 h-9 mx-auto text-green-500" />
                <p className="font-bold mt-3 text-sm">{c.title}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* 20-year journey timeline */}
    <section className="py-20 bg-white" data-testid="about-journey">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>Our Journey</SectionLabel>
          <h2 className="text-4xl font-black text-[#0e1116]">Serving every industry for the <span className="text-orange-500">last 20+ years</span></h2>
          <p className="text-gray-500 mt-3">Two decades of engineering, drafting and modelling for global teams.</p>
        </div>
        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block"></div>
          <div className="space-y-8">
            {[
              { year: '2003', title: 'Founded', desc: 'Land Drafters opens its first delivery studio with a small team focused on land surveying CAD for US developers.' },
              { year: '2008', title: 'BIM Adoption', desc: 'Early adopters of Revit & Navisworks — first federated BIM model delivered for a US healthcare campus.' },
              { year: '2012', title: 'Global Expansion', desc: 'Cross-border delivery to UK, Canada, Australia. ISO 9001 certification awarded.' },
              { year: '2016', title: 'Multi-Disciplinary', desc: 'MEP, structural, civil, plant and process engineering practices launched — single roof for end-to-end deliverables.' },
              { year: '2020', title: 'Digital Twin & Scan-to-BIM', desc: 'Laser-scan and reality-capture workflows go live. 5D BIM integrated with cost & schedule.' },
              { year: '2023', title: '20-Year Milestone', desc: '350+ engineers · 15,000+ projects · 40+ countries served. GIS & smart-cities practice launched.' },
              { year: '2026', title: 'Today', desc: 'Multilingual SEO across 20 languages, real-time visitor analytics, structured quote intake — the digital-first engineering partner.' }
            ].map((m, i) => (
              <div key={m.year} className={`md:grid md:grid-cols-2 md:gap-12 items-center ${i % 2 === 0 ? '' : 'md:[direction:rtl]'}`}>
                <div className={`bg-gray-50 border border-gray-200 rounded-lg p-6 hover:border-green-500 hover:shadow-lg transition-all ${i % 2 === 0 ? '' : 'md:[direction:ltr]'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-orange-500 text-white font-black text-lg px-3 py-1 rounded">{m.year}</span>
                    <h3 className="text-xl font-bold text-[#0e1116]">{m.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                </div>
                <div className="hidden md:flex justify-center relative">
                  <div className="w-5 h-5 rounded-full bg-gray-500 border-4 border-white shadow-md"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Lifecycle process */}
    <section className="py-20 bg-gray-50" data-testid="about-process">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>How we deliver</SectionLabel>
          <h2 className="text-4xl font-black text-[#0e1116]">Our <span className="text-green-600">Engineering Lifecycle</span></h2>
          <p className="text-gray-500 mt-3">A repeatable six-stage process that scales from a single drawing set to multi-year framework agreements.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {lifecycle.map((l, i) => (
            <div key={l.num} className="bg-white rounded-lg border border-gray-200 p-6 hover:border-orange-400 hover:shadow-md transition-all">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-black text-green-600">{l.num}</span>
                <h3 className="text-lg font-bold text-[#0e1116]">{l.title}</h3>
              </div>
              <p className="text-gray-600 text-sm mt-3 leading-relaxed">{l.desc}</p>
              <div className="w-12 h-1 bg-orange-500 mt-5"></div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Quick-link grid to sister pages */}
    <section className="py-20 bg-white" data-testid="about-explore">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>Explore more</SectionLabel>
          <h2 className="text-4xl font-black text-[#0e1116]">{tt('Get to know')} <span className="text-green-600">Land Drafters</span></h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { to: '/about/team', icon: 'Users', title: 'Our Team', desc: 'Meet the 350+ engineers behind every deliverable.', cta: 'Meet the team' },
            { to: '/about/vision', icon: 'Sparkles', title: 'Vision & Mission', desc: 'The values and vision powering 20 years of growth.', cta: 'Read our vision' },
            { to: '/why-us', icon: 'Award', title: 'Why Choose Us', desc: '14 reasons global teams trust us for critical work.', cta: 'See the difference' },
            { to: '/careers', icon: 'Briefcase', title: 'Careers', desc: 'Join the team — open roles in CAD, BIM, GIS, MEP & more.', cta: 'View openings' },
            { to: '/blogs', icon: 'BookOpen', title: 'Blogs & Insights', desc: 'Engineering deep-dives, BIM workflows, industry trends.', cta: 'Read the blog' },
            { to: '/coverage', icon: 'Globe2', title: 'Global Coverage', desc: '40+ countries · region-aware contacts · 24/7 support.', cta: 'See the map' }
          ].map(c => {
            const Ic = Icons[c.icon];
            return (
              <Link key={c.to} to={c.to} data-testid={`about-link-${c.to.replace(/[^a-z0-9]+/gi, '-')}`} className="group bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-green-500 rounded-lg p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded bg-gray-100 text-green-700 group-hover:bg-orange-500 group-hover:text-white flex items-center justify-center transition-colors">
                  <Ic className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-black text-[#0e1116] mt-5">{tt(c.title)}</h3>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">{tt(c.desc)}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-xs font-bold uppercase tracking-wider text-green-600 group-hover:text-orange-500">
                  {tt(c.cta)} <Icons.ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>

    <WhyChooseUs heading="Why Companies Choose" brand="Land Drafters" subheading="As Their Engineering Partner?" />

    <CTA />
    <FloatingActions />
  </>
  );
};

// Helpers
const Initials = ({ name }) => {
  const parts = name.replace(/^(Mr\.|Mrs\.|Ms\.)\s*/i, '').split(/\s+/);
  const ini = (parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '');
  return <>{ini.toUpperCase()}</>;
};

export const TeamPage = () => {
  useSeo({ title: 'Our Team', description: 'Meet the 350+ multidisciplinary engineers, BIM specialists and surveyors behind every Land Drafters deliverable.', path: '/about/team' });
  const totalMembers = leadership.length + Object.values(operationsTeam).reduce((n, g) => n + g.members.length, 0);
  const teamSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: leadership.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Person',
        name: p.name,
        jobTitle: p.role,
        description: p.cardBio,
        image: p.img,
        email: p.email,
        sameAs: p.linkedin ? [p.linkedin] : undefined,
        worksFor: { '@type': 'Organization', name: 'Land Drafters', url: 'https://ldglobal.co' },
        url: `https://ldglobal.co/about/team/${p.slug}`
      }
    }))
  };
  return (
  <>
    <JsonLd id="team" data={teamSchema} />
    <PageHero title="Meet" highlight="Our Expert Team" typewriter={['Leaders','Engineers','Innovators']} subtitle="Skilled professionals dedicated to delivering excellence in CAD drafting, land surveying, and engineering design." breadcrumb="Our Team" />

    {/* Leadership */}
    <section className="py-20 bg-white" data-testid="team-leadership">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-12">
          <SectionLabel>Leadership</SectionLabel>
          <h2 className="text-4xl font-black text-[#0e1116]">Our <span className="text-green-600">Leadership</span></h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Decades of combined experience driving Land Drafters forward.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {leadership.map(p => (
            <div key={p.slug} data-testid={`leader-${p.slug}`} className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-green-500 hover:shadow-2xl transition-all">
              <Link to={`/about/team/${p.slug}`} className="block relative h-80 overflow-hidden bg-gradient-to-br from-gray-50 to-amber-50">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 left-3 bg-orange-500 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded">{p.short}</div>
              </Link>
              <div className="p-6">
                <Link to={`/about/team/${p.slug}`}>
                  <h3 className="font-black text-2xl text-[#0e1116] group-hover:text-green-600 transition-colors">{p.name}</h3>
                </Link>
                <div className="text-green-600 font-bold text-sm mt-0.5">{p.role}</div>
                <p className="text-gray-600 text-sm mt-3 leading-relaxed line-clamp-3">{p.cardBio}</p>
                <div className="flex items-center gap-3 mt-5 flex-wrap">
                  <a href={`mailto:${p.email}`} data-testid={`leader-email-${p.slug}`} className="inline-flex items-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-green-700 text-xs font-bold px-3 py-2 rounded uppercase tracking-wider">
                    <Icons.Mail className="w-3.5 h-3.5" /> Email
                  </a>
                  <a href={p.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`leader-linkedin-${p.slug}`} className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold px-3 py-2 rounded uppercase tracking-wider">
                    <Icons.Linkedin className="w-3.5 h-3.5" /> LinkedIn
                  </a>
                  <Link to={`/about/team/${p.slug}`} data-testid={`leader-knowmore-${p.slug}`} className="inline-flex items-center gap-1 ml-auto text-orange-500 hover:text-green-600 text-xs font-bold uppercase tracking-wider">
                    Know More <Icons.ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Operations & Support */}
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-testid="team-operations">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-12">
          <SectionLabel>Operations & Support</SectionLabel>
          <h2 className="text-4xl font-black text-[#0e1116]">Our <span className="text-orange-500">Operations Family</span></h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Team Managers, Senior Engineers, and Junior Engineers across HRA, Marketing, Office, and Surveying Drafting.</p>
        </div>

        {/* HRA */}
        <DepartmentBlock dept={operationsTeam.hra} accent="orange" testid="dept-hra" />

        <div className="my-10">
          <h3 className="text-2xl font-black text-[#0e1116]">Surveying Drafting Department</h3>
          <p className="text-gray-500 text-sm mt-1">Team Managers, Senior Engineers, and Junior Engineers</p>
        </div>

        <DepartmentBlock dept={operationsTeam.managers} accent="green" testid="dept-managers" />
        <DepartmentBlock dept={operationsTeam.seniors}  accent="green" testid="dept-seniors" />
        <DepartmentBlock dept={operationsTeam.juniors}  accent="orange" testid="dept-juniors" />

        {/* Numbers strip */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: `${totalMembers}+`, l: 'Team Members' },
            { v: '15K+',             l: 'Projects Completed' },
            { v: '150+',             l: 'Active Clients' },
            { v: '20+',              l: 'Years Experience' }
          ].map(s => (
            <div key={s.l} className="bg-white rounded-lg p-6 text-center border border-gray-200 hover:border-green-500 transition-colors">
              <div className="text-3xl md:text-4xl font-black text-[#0e1116]">{s.v}</div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 bg-gradient-to-br from-gray-50 to-orange-50 rounded-lg p-8 md:p-12 text-center">
          <Icons.Users className="w-12 h-12 mx-auto text-green-600" />
          <h3 className="text-3xl font-black mt-4 text-[#0e1116]">Join Our <span className="text-green-600">Team</span></h3>
          <p className="text-gray-600 mt-3 max-w-2xl mx-auto">We&apos;re always looking for talented CAD professionals to join our growing team.</p>
          <Link to="/careers" data-testid="team-join-cta" className="inline-flex items-center gap-2 mt-7 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider">View Careers <Icons.ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>

    <CTA />
    <FloatingActions />
  </>
  );
};

const DepartmentBlock = ({ dept, accent, testid }) => {
  const isOrange = accent === 'orange';
  return (
    <div data-testid={testid} className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-1 ${isOrange ? 'bg-orange-500' : 'bg-gray-500'}`}></div>
        <h4 className="font-black text-xl text-[#0e1116]">{dept.title}</h4>
        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">{dept.members.length} people</span>
      </div>
      <p className="text-gray-500 text-sm mb-4">{dept.sub}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {dept.members.map(m => (
          <div key={m.name} className={`bg-white rounded-lg p-4 border ${isOrange ? 'border-orange-200 hover:border-orange-500' : 'border-gray-200 hover:border-green-500'} hover:shadow-md transition-all`}>
            <div className={`w-12 h-12 rounded-full ${isOrange ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-green-700'} flex items-center justify-center font-black text-sm`}>
              <Initials name={m.name} />
            </div>
            <div className="font-bold text-[#0e1116] text-sm mt-3 leading-tight">{m.name}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{m.role}</div>
            {m.exp && <div className="text-[10px] uppercase tracking-wider text-orange-600 font-bold mt-1">{m.exp}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const TeamMemberDetail = () => {
  const { slug } = useParams();
  const p = leadership.find(x => x.slug === slug);
  useSeo({
    title: p ? `${p.name} — ${p.role}` : 'Team Member',
    description: p ? `${p.name}, ${p.role} at Land Drafters. ${p.bio || 'Leading engineering excellence across our global delivery centers.'}` : 'Land Drafters team member profile.',
    path: `/about/team/${slug}`,
    type: 'profile'
  });
  if (!p) return <Navigate to="/about/team" replace />;
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: p.name,
    jobTitle: p.role,
    description: p.cardBio || p.short,
    image: p.img,
    email: p.email,
    sameAs: p.linkedin ? [p.linkedin] : undefined,
    worksFor: { '@type': 'Organization', name: 'Land Drafters', url: 'https://ldglobal.co' },
    url: `https://ldglobal.co/about/team/${p.slug}`
  };
  return (
    <>
      <JsonLd id={`person-${p.slug}`} data={personSchema} />
      <section className="relative pt-16 pb-12 bg-gradient-to-br from-amber-50 via-white to-gray-50 overflow-hidden" data-testid="member-hero">
        <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(148,163,184,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.10)_1px,transparent_1px)] [background-size:48px_48px] pointer-events-none"></div>
        <div className="relative max-w-[1200px] mx-auto px-6">
          <div className="text-xs text-gray-500 mb-5 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-green-600">Home</Link>
            <Icons.ChevronRight className="w-3 h-3" />
            <Link to="/about/team" className="hover:text-green-600">Team</Link>
            <Icons.ChevronRight className="w-3 h-3" />
            <span className="text-[#0e1116] font-semibold">{p.name}</span>
          </div>
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              <div className="relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                <img src={p.img} alt={p.name} className="w-full h-auto object-cover object-top" />
                <div className="absolute top-3 left-3 bg-orange-500 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded" data-testid="member-role-badge">{p.short}</div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <a href={`mailto:${p.email}`} data-testid="member-email" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-2.5 rounded uppercase tracking-wider">
                  <Icons.Mail className="w-3.5 h-3.5" /> Email
                </a>
                <a href={p.linkedin} target="_blank" rel="noopener noreferrer" data-testid="member-linkedin" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-2.5 rounded uppercase tracking-wider">
                  <Icons.Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </a>
              </div>
            </div>
            <div className="lg:col-span-2">
              <h1 className="text-4xl md:text-5xl font-black text-[#0e1116]" data-testid="member-name">{p.name}</h1>
              <div className="text-green-600 font-bold text-lg mt-1" data-testid="member-role">{p.role}</div>
              <blockquote className="mt-5 pl-4 border-l-4 border-orange-500 text-[15px] text-gray-700 italic" data-testid="member-quote">
                &ldquo;{p.quote}&rdquo;
              </blockquote>
              <div className="mt-6 space-y-4 text-gray-700 text-[15px] leading-relaxed" data-testid="member-bio">
                {p.bio.map((para) => <p key={para.slice(0, 32)}>{para}</p>)}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-orange-100 text-[#0e1116] font-bold text-sm px-4 py-2 rounded">
                <Icons.Sparkles className="w-4 h-4 text-orange-500" /> {p.tagline}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education + Expertise */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-7" data-testid="member-education">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-gray-600 text-white flex items-center justify-center"><Icons.GraduationCap className="w-5 h-5" /></div>
              <h3 className="text-2xl font-black text-[#0e1116]">Education & Qualifications</h3>
            </div>
            <ul className="space-y-3">
              {p.education.map((e) => (
                <li key={e} className="flex items-start gap-2 text-gray-700 text-sm leading-relaxed">
                  <Icons.GraduationCap className="w-4 h-4 text-green-600 mt-0.5 shrink-0" /> {e}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl border border-orange-200 p-7" data-testid="member-expertise">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center"><Icons.Award className="w-5 h-5" /></div>
              <h3 className="text-2xl font-black text-[#0e1116]">Key Areas of Expertise</h3>
            </div>
            <ul className="space-y-3">
              {p.expertise.map((e) => (
                <li key={e} className="flex items-start gap-2 text-gray-700 text-sm leading-relaxed">
                  <Icons.CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> {e}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-black text-[#0e1116]">Want to Work with {p.name.split(' ')[0]}?</h3>
          <p className="text-gray-600 mt-2">Get in touch to discuss your project requirements.</p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/get-quote" data-testid="member-quote-cta" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider">Get a Quote <Icons.ArrowRight className="w-3.5 h-3.5" /></Link>
            <Link to="/about/team" data-testid="member-back-team" className="inline-flex items-center gap-2 border-2 border-[#0e1116] hover:bg-[#0e1116] hover:text-white text-[#0e1116] font-bold px-6 py-3 rounded uppercase text-xs tracking-wider">Back to Team</Link>
          </div>
        </div>
      </section>

      <CTA />
      <FloatingActions />
    </>
  );
};

export const VisionMission = () => {
  useSeo({ title: 'Vision & Mission', description: 'The values, vision, and mission that guide Land Drafters\' 20-year engineering legacy across 40+ countries.', path: '/about/vision' });
  return (
  <>
    <PageHero title="Vision &" highlight="Mission" typewriter={['Our Purpose','Our Drive','Our Promise']} subtitle="What we stand for and where we are going — the principles that guide every Land Drafters project, every day." breadcrumb="Vision & Mission" />
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-6 space-y-12">
        {[
          { icon: 'Eye', title: 'Our Vision', desc: 'To empower businesses worldwide through talent and passion. We envision a future where every engineering challenge — from a single-family home to a city-scale infrastructure project — has access to expert design and drafting capabilities, delivered with the highest quality and reliability.' },
          { icon: 'Target', title: 'Our Mission', desc: 'Enduring partnerships that enable continuous growth for our customers, Land Drafters team, and other stakeholders. We commit to delivering technically robust, cost-efficient, and scalable engineering solutions across the complete project lifecycle.' },
          { icon: 'Heart', title: 'Our Philosophy: Grow Together', desc: 'We believe in collaborative partnerships rooted in trust, transparency, and shared success. By empowering our people and embracing our customers as partners, we create environments where everyone thrives — enabling sustained growth for years to come.' }
        ].map((v, i) => {
          const Ic = Icons[v.icon];
          return (
            <div key={v.title} className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-center`}>
              <div className="lg:w-2/5">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-600 to-gray-500 flex items-center justify-center mx-auto">
                  <Ic className="w-14 h-14 text-white" />
                </div>
              </div>
              <div className="lg:w-3/5">
                <h2 className="text-3xl font-black text-[#0e1116]">{v.title}</h2>
                <p className="text-gray-600 mt-4 leading-relaxed">{v.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
    <CTA />
    <FloatingActions />
  </>
  );
};

export const WhyUs = () => {
  useSeo({ title: 'Why Choose Us', description: '14 reasons global teams trust Land Drafters with their most critical engineering and CAD drafting projects.', path: '/why-us' });
  return (
  <>
    <PageHero title="Why" highlight="Choose Us" typewriter={['Quality First','On-Time','Accurate']} subtitle="The capabilities that make Land Drafters the preferred CAD, BIM, and engineering partner for 350+ firms worldwide." breadcrumb="Why Us?" />
    <section className="py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-10">
        {[
          { icon: 'Award', title: 'Survey-Grade Accuracy', desc: 'Sub-centimeter precision using RTK GPS, total stations, laser scanners, and drone-based photogrammetry.' },
          { icon: 'Clock', title: '24-48hr Turnaround', desc: 'Fast delivery on standard projects, with rush options on critical-path drawings and emergency revisions.' },
          { icon: 'BadgeCheck', title: 'Licensed Engineers', desc: 'PE & PLS stamped deliverables for projects that require formal certification across multiple US states.' },
          { icon: 'Layers3', title: 'Full BIM/CAD Stack', desc: 'AutoCAD, Civil 3D, Revit, Tekla, Navisworks, MicroStation, SolidWorks — one vendor, every tool.' },
          { icon: 'ShieldCheck', title: 'ISO 9001:2015 Quality', desc: 'Process-driven quality management with a proprietary 3-stage QC system ensuring error-free output.' },
          { icon: 'Globe2', title: 'Global Delivery Model', desc: 'US-based engineering oversight combined with cost-efficient global execution centers in India.' },
          { icon: 'Lock', title: 'Secure Data Handling', desc: 'Enterprise-grade data security, NDAs, and confidentiality protocols for every client engagement.' },
          { icon: 'TrendingUp', title: 'Scalable Resource Model', desc: 'Dedicated 160 hr/month resources or project-based teams — scale up or down as your pipeline requires.' }
        ].map((f) => {
          const Ic = Icons[f.icon];
          return (
            <div key={f.title} className="flex gap-5 p-6 bg-gray-50 rounded-lg hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-green-200">
              <div className="w-14 h-14 rounded bg-gradient-to-br from-gray-600 to-gray-500 text-white flex items-center justify-center shrink-0"><Ic className="w-7 h-7" /></div>
              <div>
                <h3 className="font-bold text-xl text-[#0e1116]">{f.title}</h3>
                <p className="text-gray-600 mt-2 text-sm leading-relaxed">{f.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>

    {/* Lifecycle */}
    <section className="py-20 bg-[#0e1116] text-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>Project Lifecycle Support</SectionLabel>
          <h2 className="text-4xl font-black">From Tendering to <span className="text-orange-500">Operations</span></h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {lifecycle.map((p) => (
            <div key={p.num} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-5 hover:border-orange-500 transition-all">
              <div className="text-orange-500 font-black text-lg mb-2">{p.num}</div>
              <h4 className="font-bold text-sm">{p.title}</h4>
              <p className="text-gray-400 text-xs mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <CTA />
    <FloatingActions />
  </>
  );
};

export const IndustriesPage = () => {
  useSeo({ title: 'Industries We Serve', description: 'Explore the verticals Land Drafters supports — real estate, infrastructure, energy, manufacturing, oil & gas, healthcare and more.', path: '/industries' });
  const SECTOR_GROUPS = {
    'Built Environment': ['smart-cities', 'real-estate', 'construction', 'aec', 'residential-commercial'],
    'Infrastructure & Public Works': ['government', 'transportation', 'water', 'telecom'],
    'Energy & Industrial': ['oil-gas', 'industrial', 'renewable', 'environmental'],
    'Digital & Specialised': ['bim-gis']
  };
  return (
    <>
      <PageHero title="Industries" highlight="We Serve" typewriter={['Diverse Sectors','Global Markets','Every Discipline']} subtitle="Domain depth across construction, infrastructure, real estate, environmental, energy, manufacturing, and more." breadcrumb="Industries" />

      {/* Intro + stats */}
      <section className="py-14 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-3 gap-10 items-center">
          <div className="lg:col-span-2">
            <SectionLabel>Verticals</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0e1116] mt-3 leading-tight">
              {industries.length} industries.<br />
              <span className="text-green-600">One trusted</span> <span className="text-orange-500">engineering partner.</span>
            </h2>
            <p className="text-gray-600 mt-5 leading-relaxed max-w-3xl">
              Over two decades we&apos;ve delivered for smart cities, infrastructure agencies, real-estate developers, energy operators, and industrial plants across 40+ countries. Each vertical brings its own codes, software, and conventions — our teams speak all of them fluently.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/contact" data-testid="industries-talk-expert" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider">Talk to a Sector Expert <Icons.ArrowRight className="w-4 h-4" /></Link>
              <Link to="/get-quote" className="inline-flex items-center gap-2 border-2 border-[#0e1116] hover:bg-[#0e1116] hover:text-white text-[#0e1116] font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider">Request a Quote</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: 'Globe', val: '40+', sub: 'Countries served' },
              { icon: 'Building', val: '15K+', sub: 'Projects delivered' },
              { icon: 'Users', val: '2,500+', sub: 'Repeat clients' },
              { icon: 'Award', val: '20+', sub: 'Years of practice' }
            ].map(k => {
              const Ic = Icons[k.icon] || Icons.Sparkles;
              return (
                <div key={k.val} className="bg-gray-50 hover:bg-gray-50 border border-gray-200 hover:border-green-400 rounded-lg p-4 transition-colors">
                  <Ic className="w-6 h-6 text-orange-500" />
                  <div className="font-black text-[#0e1116] mt-2 text-2xl leading-none">{k.val}</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{k.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grouped sectors */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          {Object.entries(SECTOR_GROUPS).map(([sector, slugs]) => {
            const list = industries.filter(i => slugs.includes(i.slug));
            if (list.length === 0) return null;
            return (
              <div key={sector} className="mb-14" data-testid={`ind-sector-${sector.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-1 bg-orange-500"></div>
                  <h2 className="text-2xl md:text-3xl font-black text-[#0e1116]">{sector}</h2>
                  <span className="text-xs uppercase tracking-wider font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">{list.length} verticals</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {list.map(ind => {
                    const Ic = Icons[ind.icon] || Icons.Briefcase;
                    return (
                      <Link
                        key={ind.slug}
                        to={`/industries/${ind.slug}`}
                        id={ind.slug}
                        data-testid={`industry-${ind.slug}`}
                        className="group p-7 bg-white rounded-lg border border-gray-200 hover:border-green-500 hover:bg-[#0e1116] hover:text-white transition-all duration-300 cursor-pointer scroll-mt-32 block"
                      >
                        <div className="w-14 h-14 rounded bg-gray-100 group-hover:bg-gray-600 text-green-700 group-hover:text-white flex items-center justify-center transition-colors"><Ic className="w-7 h-7" /></div>
                        <h3 className="font-bold text-lg mt-5">{ind.title}</h3>
                        <p className="text-sm text-gray-500 group-hover:text-orange-300 mt-2">{ind.desc}</p>
                        <div className="flex items-center gap-3 mt-5 text-xs font-bold uppercase tracking-wider">
                          <span className="text-green-600 group-hover:text-green-400 inline-flex items-center gap-1">Explore <Icons.ArrowRight className="w-3 h-3" /></span>
                          <span className="text-gray-300 group-hover:text-orange-600">|</span>
                          <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = '/get-quote'; }} className="text-orange-500 group-hover:text-orange-400 hover:text-green-500 inline-flex items-center gap-1 cursor-pointer">Quote <Icons.ArrowRight className="w-3 h-3" /></span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Software fluency — real brand logos */}
      <SoftwareLogos heading="Software We Use" subheading="Real tools, real fluency — your stack already lives in our delivery teams." />

      <WhyChooseUs heading="Why Industry Leaders Choose" brand="Land Drafters" subheading="As Their CAD Partner?" />

      <CTA />
      <FloatingActions />
    </>
  );
};

// ===================================================================
// IndustryDetail — per-industry page with linked services
// ===================================================================
export const IndustryDetail = () => {
  const { slug } = useParams();
  const ind = industries.find(i => i.slug === slug);
  const det = INDUSTRY_DETAILS[slug];
  useSeo({
    title: ind ? `${ind.title} — Industries` : 'Industry',
    description: ind ? (ind.desc || `Specialized engineering and CAD drafting services for the ${ind.title} sector by Land Drafters.`) : 'Land Drafters industry vertical.',
    path: `/industries/${slug}`
  });
  const { tt } = useLang();
  if (!ind || !det) return <Navigate to="/industries" replace />;

  const Ic = Icons[ind.icon] || Icons.Briefcase;
  const linkedServices = det.services
    .map(s => services.find(x => x.slug === s))
    .filter(Boolean);
  const related = industries.filter(i => i.slug !== slug).slice(0, 6);

  return (
    <>
      {/* Hero */}
      <section className="relative bg-[#0e1116] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-50">
          <img src={det.hero} alt={ind.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0e1116] via-[#0e1116]/85 to-[#0e1116]/30"></div>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 py-24">
          <Link to="/industries" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-green-400 hover:text-orange-400 mb-5">
            <Icons.ArrowLeft className="w-3.5 h-3.5" /> {tt('All Industries')}
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded bg-gray-600 flex items-center justify-center shadow-lg shadow-gray-600/30"><Ic className="w-8 h-8 text-white" /></div>
            <div className="text-orange-400 text-[11px] uppercase tracking-[0.25em] font-bold">{tt(det.tagline)}</div>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight max-w-3xl">{tt(ind.title)}</h1>
          <p className="text-gray-300 mt-6 max-w-3xl text-lg leading-relaxed">{tt(det.summary)}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/get-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors" data-testid="industry-quote-btn">
              {tt('Request a Quote')} <Icons.ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:border-green-500 hover:text-green-400 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors">
              {tt('Talk to a Sector Expert')}
            </Link>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-gradient-to-r from-gray-600 to-gray-700 text-white py-8">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-2 md:grid-cols-3 gap-6">
          {det.stats.map(s => (
            <div key={s.label} className="text-center">
              <div className="text-3xl md:text-4xl font-black">{s.val}</div>
              <div className="text-xs uppercase tracking-wider opacity-90 mt-1">{tt(s.label)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Global Operations narrative */}
      {det.globalOps && (
        <section className="py-16 md:py-20 bg-[#0e1116] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(148,163,184,0.18),transparent_60%)] pointer-events-none"></div>
          <div className="relative max-w-[1400px] mx-auto px-6 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="inline-flex items-center gap-2 text-orange-400 text-[11px] uppercase tracking-[0.25em] font-bold mb-3">
                <Icons.Globe2 className="w-4 h-4" /> {tt('Global Operations')}
              </div>
              <h2 className="text-3xl md:text-4xl font-black leading-tight">
                {tt('How')} <span className="text-green-500">{tt(ind.title)}</span> {tt('runs globally')}
              </h2>
              <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-wider text-gray-400">
                <span className="inline-flex items-center gap-1.5"><Icons.MapPin className="w-3.5 h-3.5 text-orange-400" /> 40+ {tt('countries')}</span>
                <span className="inline-flex items-center gap-1.5"><Icons.Clock className="w-3.5 h-3.5 text-green-400" /> 24-48{tt('hr TAT')}</span>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="border-l-2 border-gray-500/60 pl-6">
                <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-testid="industry-global-ops">{tt(det.globalOps)}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Challenges + Deliverables */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <div>
            <SectionLabel>Sector Challenges</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0e1116] mt-2">{tt('What this sector')} <span className="text-orange-500">{tt('demands')}</span></h2>
            <ul className="mt-6 space-y-3">
              {det.challenges.map((c, i) => (
                <li key={c} className="flex gap-3 text-gray-700">
                  <span className="w-7 h-7 shrink-0 rounded bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm">{i + 1}</span>
                  <span className="text-sm leading-relaxed">{tt(c)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionLabel>What We Deliver</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0e1116] mt-2">{tt('Drawings, models &')} <span className="text-green-600">{tt('deliverables')}</span></h2>
            <ul className="mt-6 space-y-3">
              {det.deliverables.map((d) => (
                <li key={d} className="flex gap-3 text-gray-700">
                  <Icons.CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{tt(d)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Linked services */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Services for this sector</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black text-[#0e1116]">{tt('Engineering services for')} <span className="text-green-600">{tt(ind.title)}</span></h2>
            <p className="text-gray-500 mt-4">{tt('The complete service stack we deploy on projects in this sector. Click any to dive into its capabilities, sub-services, and process.')}</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {linkedServices.map(s => {
              const SIc = Icons[s.icon] || Icons.Box;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`ind-svc-${s.slug}`}
                  className="group bg-white border border-gray-200 hover:border-green-500 rounded-lg p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded bg-gray-100 text-green-700 group-hover:bg-orange-500 group-hover:text-white flex items-center justify-center transition-colors">
                    <SIc className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-[#0e1116] mt-4 group-hover:text-green-600 transition-colors">{tt(s.title)}</h3>
                  <p className="text-xs text-gray-500 mt-2 leading-snug line-clamp-3">{tt(s.short)}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold uppercase tracking-wider text-green-600 group-hover:text-orange-500">
                    {tt('Explore service')} <Icons.ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-orange-500 hover:text-green-600">
              {tt('Browse all 28 services')} <Icons.ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related industries */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-10">
            <SectionLabel>Other Industries</SectionLabel>
            <h2 className="text-2xl md:text-3xl font-black text-[#0e1116]">{tt('Sectors we also serve')}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {related.map(r => {
              const RIc = Icons[r.icon] || Icons.Briefcase;
              return (
                <Link
                  key={r.slug}
                  to={`/industries/${r.slug}`}
                  data-testid={`rel-ind-${r.slug}`}
                  className="group bg-gray-50 hover:bg-[#0e1116] hover:text-white border border-gray-200 hover:border-green-500 rounded p-4 text-center transition-all"
                >
                  <RIc className="w-7 h-7 mx-auto text-green-600 group-hover:text-orange-400 transition-colors" />
                  <p className="text-xs font-bold mt-2 leading-tight text-[#0e1116] group-hover:text-white">{tt(r.title)}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTA />
      <FloatingActions />
    </>
  );
};
